import type { ScheduleRow } from "../types";
import { formatShiftTimeRange, shiftSimilarityKey } from "../util/shiftTimeFormat";

/**
 * Build a popularity-driven alias map for shift-time display values (mapping §8.2 Case A).
 *
 * Two shifts collapse together iff they share the exact start time and the same end hour
 * (end minute may differ — see `shiftSimilarityKey`). Within each similarity group, the
 * display value with the most rows wins; every other display value in the group aliases
 * to the winner.
 *
 * Returns an empty map when no group has more than one variant (no merges possible).
 */
export const buildShiftTimeDisplayAliasMap = (rows: ScheduleRow[], timezone: string): Map<string, string> => {
	const alias = new Map<string, string>();

	const similarityGroups = new Map<string, Map<string, number>>();
	for (const row of rows) {
		const key = shiftSimilarityKey(row.shiftStart, row.shiftEnd);
		const display = formatShiftTimeRange(row.shiftStart, row.shiftEnd, timezone);
		let counts = similarityGroups.get(key);
		if (!counts) {
			counts = new Map();
			similarityGroups.set(key, counts);
		}
		counts.set(display, (counts.get(display) ?? 0) + 1);
	}

	for (const [, counts] of similarityGroups) {
		if (counts.size <= 1) continue;
		let largestDisplay = "";
		let largestCount = -1;
		for (const [display, count] of counts) {
			if (count > largestCount) {
				largestCount = count;
				largestDisplay = display;
			}
		}
		for (const [display] of counts) {
			if (display !== largestDisplay) alias.set(display, largestDisplay);
		}
	}

	return alias;
};

/**
 * Build a popularity-driven alias map for anchor-row keys when shift time is an anchor column
 * (mapping §8.2 Case B).
 *
 * The "prefix key" for similarity is built from all anchor columns: exact values for everything
 * except shift time, where the value is `start + floor-to-hour end`. Anchor keys within the
 * same prefix bucket collapse to the most-populated anchor key in that bucket.
 */
export interface BuildAnchorAliasMapArgs {
	rows: ScheduleRow[];
	rawAnchorKey: (row: ScheduleRow) => string;
	shiftTimeAnchorIdx: number;
	otherAnchorPart: (row: ScheduleRow, anchorIdx: number) => string;
}

export const buildAnchorShiftTimeAliasMap = (args: BuildAnchorAliasMapArgs): Map<string, string> => {
	const { rows, rawAnchorKey, shiftTimeAnchorIdx, otherAnchorPart } = args;
	const alias = new Map<string, string>();
	if (shiftTimeAnchorIdx < 0) return alias;

	const prefixKeyFor = (row: ScheduleRow): string => {
		// Build a prefix key where the shiftTime slot uses (start, end-hour) and other slots use their exact values.
		// `otherAnchorPart` callback abstracts the per-anchor value lookup so the caller owns column-kind knowledge.
		const parts: string[] = [];
		const totalAnchors = rawAnchorKey(row).split("|").length;
		for (let i = 0; i < totalAnchors; i++) {
			parts.push(i === shiftTimeAnchorIdx ? `${row.shiftStart.toISO()}~${row.shiftEnd.startOf("hour").toISO()}` : otherAnchorPart(row, i));
		}
		return parts.join("|");
	};

	const prefixGroups = new Map<string, Map<string, number>>();
	for (const row of rows) {
		const prefix = prefixKeyFor(row);
		const key = rawAnchorKey(row);
		let counts = prefixGroups.get(prefix);
		if (!counts) {
			counts = new Map();
			prefixGroups.set(prefix, counts);
		}
		counts.set(key, (counts.get(key) ?? 0) + 1);
	}

	for (const [, counts] of prefixGroups) {
		if (counts.size <= 1) continue;
		let largestKey = "";
		let largestCount = -1;
		for (const [key, count] of counts) {
			if (count > largestCount) {
				largestCount = count;
				largestKey = key;
			}
		}
		for (const [key] of counts) {
			if (key !== largestKey) alias.set(key, largestKey);
		}
	}

	return alias;
};
